<template>
  <div
    ref="container"
    class="container"
    :class="{
      resizing: gridAction === GridAction.ResizingRange,
      selecting: gridAction === GridAction.Selecting,
      readonly: isReadOnly,
    }">
    <!-- Actual grid containing the Time Ranges -->
    <div
      v-show="!isGameRunning"
      ref="grid"
      class="grid"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseLeave">
      <!-- Selected Time Ranges -->
      <template
        v-for="(daySchedule, dayIndex) in schedule.daySchedules"
        :key="dayIndex">
        <template
          v-for="(timeRange, rangeIndex) in daySchedule.scheduledRanges"
          :key="rangeIndex">
          <Range
            :day="dayIndex"
            :time-range="timeRange"
            :grid-action="gridAction"
            :is-read-only="isReadOnly"
            @resize-started="startRangeResizing" />
        </template>
      </template>

      <!-- Preview of selected Time Ranges -->
      <template v-if="gridAction === GridAction.Selecting">
        <template
          v-for="(daySchedule, dayIndex) in previewRange"
          :key="dayIndex">
          <template
            v-for="(timeRange, rangeIndex) in daySchedule"
            :key="rangeIndex">
            <Range
              :day="dayIndex"
              :time-range="timeRange"
              :grid-action="gridAction"
              :preview="true"
              :selecting="isSelecting" />
          </template>
        </template>
      </template>
    </div>
    <!-- Canvas displaying the grid layout -->
    <GridOverlay
      :cell-height="cellHeight"
      :cell-width="cellWidth"
      :pixel-ratio="pixelRatio"
      :show-quarter-lines="timeInterval === 'quarter'"
      @game-started="isGameRunning = true"
      @game-ended="isGameRunning = false" />
    <!-- Canvas displaying the selected area -->
    <SelectionBox
      v-if="gridAction === GridAction.Selecting"
      :start-position="startMousePosition"
      :current-position="currentMousePosition"
      :pixel-ratio="pixelRatio" />
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, Ref, ref } from 'vue';

  import WeekSchedule, { IDaypartRange } from '@/common/models/daypart/WeekSchedule';
  import { Time } from '@/common/models/Time';
  import TimeRange from '@/common/models/TimeRange';

  import GridAction from './GridAction.enum';
  import GridOverlay from './GridOverlay.vue';
  import Range, { IRangeResizeHandler } from './Range.vue';
  import SelectionBox from './SelectionBox.vue';

  export interface ICoordinates {
    x: number;
    y: number;
  }

  interface IDaypartCoordinate {
    day: number;
    time: Time;
  }

  interface IDaypartGridProps {
    schedule: WeekSchedule;
    timeInterval: 'hour' | 'quarter';
    isReadOnly?: boolean;
  }

  const props = defineProps<IDaypartGridProps>();

  interface IDaypartGridEmits {
    (e: 'areaSelected', value: IDaypartRange, selected: boolean): void;
    (e: 'rangeResized'): void;
  }

  const emits = defineEmits<IDaypartGridEmits>();

  const container: Ref<HTMLElement | null> = ref(null);
  const containerWidth: Ref<number> = ref(0);
  const containerHeight: Ref<number> = ref(0);
  const pixelRatio = window.devicePixelRatio || 1;
  const NUM_DAYS = 7;
  const NUM_HOURS = 24;
  let resizeObserver: ResizeObserver;
  const gridAction: Ref<GridAction> = ref(GridAction.None);
  const isGameRunning = ref(false);

  // region Range Resizing Data
  let resizeHandler: IRangeResizeHandler | null = null;
  // endregion

  // region Box Selection Data
  const selectedArea: Ref<IDaypartRange | null> = ref(null);
  const isSelecting = ref(false);
  let startMousePosition: ICoordinates | null = null;
  let currentMousePosition: ICoordinates | null = null;
  let startCellPosition: IDaypartCoordinate | null = null;
  let currentCellPosition: IDaypartCoordinate | null = null;
  // endregion

  const cellWidth = computed(() => containerWidth.value / NUM_HOURS);

  const cellHeight = computed(() => containerHeight.value / NUM_DAYS);

  const previewRange = computed(() => {
    const output: TimeRange[][] = Array(NUM_DAYS)
      .fill(null)
      .map(() => []);
    if (selectedArea.value == null) {
      return output;
    }
    for (let day = 0; day < NUM_DAYS; day += 1) {
      if (selectedArea.value.dayRange.from <= day && day <= selectedArea.value.dayRange.to) {
        output[day] = [selectedArea.value.timeRange.copy()];
      }
    }
    return output;
  });

  function getMouseCoordinates(event: MouseEvent): ICoordinates {
    const rect = container.value!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    return { x, y };
  }

  function getGridCoordinates(event: MouseEvent): IDaypartCoordinate {
    const { x, y } = getMouseCoordinates(event);
    const hours = x / cellWidth.value;
    const time = Time.fromObject({ hours });
    const day = Math.floor(y / cellHeight.value);
    return { day, time };
  }

  function getClosestValidTime(time: Time): Time {
    switch (props.timeInterval) {
      case 'hour': {
        if (time.minutes > 30) {
          return Time.fromObject({ hours: time.hours + 1 });
        }
        return Time.fromObject({ hours: time.hours });
      }
      case 'quarter': {
        const clampedMinutes = Math.round(time.minutes / 15) * 15;
        return Time.fromObject({ hours: time.hours, minutes: clampedMinutes });
      }
      default: {
        return time;
      }
    }
  }

  function resetGridAction() {
    gridAction.value = GridAction.None;
    resizeHandler?.stop();
    resizeHandler = null;
    selectedArea.value = null;
    isSelecting.value = false;
    startMousePosition = null;
    currentMousePosition = null;
    startCellPosition = null;
    currentCellPosition = null;
  }

  // region Range Resizing

  function startRangeResizing(rangeResizeHandler: IRangeResizeHandler) {
    resizeHandler = rangeResizeHandler;
    gridAction.value = GridAction.ResizingRange;
  }

  function updateRangeResizing(event: MouseEvent) {
    const mouseCursorTime = getGridCoordinates(event).time;
    const closestValidTime = getClosestValidTime(mouseCursorTime);
    resizeHandler!.updateRange(closestValidTime);
  }

  function stopRangeResizing() {
    const resizedDayIndex = resizeHandler!.getDayIndex();
    const originalRange = resizeHandler!.getOriginalRange();
    const newRange = resizeHandler!.getResizedRange();
    props.schedule.daySchedules[resizedDayIndex].freeRange(originalRange);
    props.schedule.daySchedules[resizedDayIndex].scheduleRange(newRange);
    resizeHandler!.stop();
    emits('rangeResized');
    resetGridAction();
  }

  // endregion

  // region Cell click

  function startCellClick(event: MouseEvent) {
    gridAction.value = GridAction.ClickCell;
    startMousePosition = getMouseCoordinates(event);
    startCellPosition = getGridCoordinates(event);
    isSelecting.value = props.schedule.isFreeAt(startCellPosition.day, startCellPosition.time);
  }

  function stopCellClick() {
    const range = {
      dayRange: { from: startCellPosition!.day, to: startCellPosition!.day },
      timeRange: TimeRange.fromObject([
        { hours: startCellPosition!.time.hours },
        { hours: startCellPosition!.time.hours + 1 },
      ]),
    };
    emits('areaSelected', range, isSelecting.value);
    resetGridAction();
  }

  // endregion

  // region Box Selection

  function getSelectedBox(): IDaypartRange {
    let [from, to] =
      startCellPosition!.time <= currentCellPosition!.time
        ? [startCellPosition!.time, currentCellPosition!.time]
        : [currentCellPosition!.time, startCellPosition!.time];

    if (props.timeInterval === 'hour') {
      from = Time.fromObject({ hours: from.hours });
      to = Time.fromObject({ hours: to.hours + 1 });
    }
    if (props.timeInterval === 'quarter') {
      from = Time.fromObject({ hours: from.hours, minutes: Math.floor(from.minutes / 15) * 15 });
      to = Time.fromObject({ hours: to.hours, minutes: Math.ceil(to.minutes / 15) * 15 });
    }

    const timeRange = TimeRange.fromObject([from, to]);
    const dayRange = {
      from: Math.min(startCellPosition!.day, currentCellPosition!.day),
      to: Math.max(startCellPosition!.day, currentCellPosition!.day),
    };
    return {
      dayRange,
      timeRange,
    };
  }

  function updateBoxSelection(event: MouseEvent) {
    if (startCellPosition == null) {
      return;
    }
    currentMousePosition = getMouseCoordinates(event);
    currentCellPosition = getGridCoordinates(event);
    selectedArea.value = getSelectedBox();
  }

  function stopBoxSelection() {
    emits('areaSelected', selectedArea.value!, isSelecting.value);
    resetGridAction();
  }

  // endregion

  function onMouseDown(event: MouseEvent) {
    if (props.isReadOnly) {
      return;
    }
    if (gridAction.value !== GridAction.None) {
      return;
    }
    startCellClick(event); // By default a mouse down event will trigger a cell click
  }

  function onMouseMove(event: MouseEvent) {
    if (props.isReadOnly) {
      return;
    }
    switch (gridAction.value) {
      case GridAction.Selecting: {
        updateBoxSelection(event);
        break;
      }
      case GridAction.ResizingRange: {
        updateRangeResizing(event);
        break;
      }
      case GridAction.ClickCell: {
        // If mouse is moved during a click, it becomes a selection
        gridAction.value = GridAction.Selecting;
        updateBoxSelection(event);
        break;
      }
      default: {
        break;
      }
    }
  }

  function onMouseUp() {
    if (props.isReadOnly) {
      return;
    }
    switch (gridAction.value) {
      case GridAction.Selecting: {
        stopBoxSelection();
        break;
      }
      case GridAction.ResizingRange: {
        stopRangeResizing();
        break;
      }
      case GridAction.ClickCell: {
        stopCellClick();
        break;
      }
      default: {
        break;
      }
    }
  }

  function onMouseLeave() {
    if (props.isReadOnly) {
      return;
    }
    resetGridAction();
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver(([observerEntry]) => {
      containerHeight.value = observerEntry.contentRect.height;
      containerWidth.value = observerEntry.contentRect.width;
    });
    resizeObserver.observe(container.value!);
  });

  onUnmounted(() => {
    resizeObserver.disconnect();
  });
</script>

<style scoped lang="scss">
  .container {
    position: absolute;
    height: 100%;
    width: 100%;
    padding: 0;

    &.resizing {
      cursor: col-resize !important;
    }

    &.selecting {
      cursor: crosshair !important;
    }

    &.readonly {
      pointer-events: none;
    }
  }

  .grid {
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns: repeat(24, auto);
    grid-template-rows: repeat(7, 1fr);
  }
</style>
